import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouteMatch, Route, Switch, useHistory, useParams } from "react-router-dom";

import { fetchStoryboards, invalidateStoryboardsState } from "actions/storyboard";
import { STORYBOARD_CREATED } from "constants/postMessageTypes";
import { createMixpanelEvent } from "actions/mixpanel";
import _ from "lodash";
import cx from "classnames";
import dateformat from "dateformat";
import { useDrag } from 'react-dnd'
import { getEmptyImage } from "react-dnd-html5-backend";

import {
  Row,
  Col,
  ProgressBar,
  Form,
  FormControl,
  OverlayTrigger,
  Popover,
  ListGroup,
  InputGroup,
  Dropdown,
  Button,
  Offcanvas
} from "react-bootstrap";

import Tooltip from "components/Tooltip";
import LinesEllipsis from 'react-lines-ellipsis'
import { Loader } from "components/ApplicationLoader";
import StoryboardsSettings from 'pages/storyboards/storyboardSettings'
import InfiniteScroll from "components/InfiniteScroll";
import { CreateStoryboard } from './templates';
import { ChoosePPTContentTypePanel, PPTXButton } from './pptxButton';

import { getBaseUrl } from "services/util";
import userRoleService from 'services/userRoles.service';
import storyboardRoleService from 'services/storyboardRoles.service';
import { MIXPANEL_PAGE_VIEWED_MY_STORYBOARD, MIXPANEL_PAGE_VIEWED } from "constants/mixpanelEvents";
import { getPageViewedEventPayload } from 'services/mixpanelUtil';

import styles from "./storyboards.module.scss";
import { DeleteStoryboard } from "./delete";
import { IKImage } from "imagekitio-react";
import StoryboardSmartList from './storyboardList/index';

import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import StoryboardCustomDragLayer from "./storyboardList/storyboardDragLayer";
import { FigmaLinkModal, FigmaFramesModal, FontsModal, ChooseContentTypePanel } from "./figma";
import { fetchIntegrations } from "actions/integrations/integrations";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import Truncate from "react-truncate";
import { INTERACTIVE_CONTENT_TYPES, STORYBOARD_TYPES } from "constants/storyboardType";

export default function StoryboardsView(props) {
  const { path } = useRouteMatch();

  return (
    <Switch>
      <Route path={`${path}/folder/:listId`}>
        <StoryboardListView />
      </Route>
      <Route path={`${path}/:storyboardId`}>
        <StoryboardsSettings />
      </Route>
      <Route exact path={path}>
        <StoryboardListView />
      </Route>
    </Switch>
  );
}

const StoryboardListView = () => {
  let { url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  useEffect(() => {
    var mixPanelPayload = getPageViewedEventPayload(MIXPANEL_PAGE_VIEWED_MY_STORYBOARD, workspace.id, workspace.name);
    dispatch(createMixpanelEvent(MIXPANEL_PAGE_VIEWED, mixPanelPayload));
  }, []);
  
  const dispatch = useDispatch();
  const { listId } = useParams();

  const [state, setState] = useState({
    create: false,
    delete: null
  });

  const [modalState, setModalState] = useState({
    showLinkModal: false,
    showFramesModal: false,
    showFontsModal: false,
    showContentTypePanel: false
  })

  const [pptState, setPptState] = useState({
    showPPTModal: false,
    pptFile: null
  });

  const [storyboardState, setStoryboardState] = useState({
    content: null,
    storyboardName: ''
  })

  const filterTypeOption = [
    {
      label:'All Types',
      value:''
    },
    {
      label: 'Spatial',
      value: 'SPATIAL'
    },
    {
      label: 'Flipbook',
      value: 'FLIP_BOOK'
    },
    {
      label: 'Interactive Content',
      value: 'INTERACTIVE_CONTENT'
    },
    {
      label: 'Landing Page',
      value: 'LANDING_PAGE'
    },
    {
      label: 'Resource Center',
      value: 'RESOURCE_CENTER'
    }
  ]

  const storyboards = useSelector(state => _.get(state, 'storyboards.items', []));
  const uploads = useSelector(state => _.get(state, 'storyboards.uploads', []));
  const loading = useSelector(state => _.get(state, 'storyboards.loading', false));
  const pagination = useSelector(state => _.get(state, 'storyboards.pagination', {}));
  const hasMoreItems = _.get(pagination, "current") < _.get(pagination, "totalPages") - 1;

  const scrollContainerRef = React.useRef(null);
  const [stickHeader, setStickHeader] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState({ label:'All Types', value:''});

  const draftFilterOptions = [
    { label: "All Status", value: null },
    { label: "Live", value: "LIVE" },
    { label: "Draft", value: "DRAFT" },
    { label: "Both", value: "BOTH" },
  ];
  
  const [draftFilterType, setDraftFilterType] = useState({ label:'All Status', value: null});
  const [showImportDropdown, setShowImportDropdown] = useState(false);

  const addedToStoryboardList = useSelector((state) => _.get(state, "storyboardList.addStoryboard.added", false));
  const deletedStoryboardList = useSelector((state) => _.get(state, "storyboardList.item.delete.deleted", false));

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const subscription = _.get(workspace, 'subscription', {});
  const workspaces = useSelector((state) => _.get(state, "workspaces.items", null));
  const promptBasedEditorEnabled = useSelector(state => _.get(state, 'workspaces.workspace.item.promptBasedEditorEnabled', false));
  const history = useHistory();

  useEffect(() => {
    dispatch(invalidateStoryboardsState())
    handleFetchStoryboards(listId, 0)
  }, [listId]);

  useEffect(() => {
    if (deletedStoryboardList) {
      history.push(`/app/${workspace.id}/storyboards`);
    }
  }, [deletedStoryboardList]);

  useEffect(()=>{
    handleFetchStoryboards(listId, 0)
  },[filterType, searchQuery, draftFilterType])

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === STORYBOARD_CREATED) {
        handleFetchStoryboards(listId, 0);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [listId]);

  const loadMoreStoryboards = () => {
    const nextPage = pagination.current + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }

    handleFetchStoryboards(listId, nextPage);
  }

  const handleFetchStoryboards = (listId, page) => {
    var request = {
      list: listId,
      page: page,
      limit: 10,
      query: searchQuery.trimStart(),
      sort: 'createdDate',
      type: filterType.value,
      order: 'desc',
      status: draftFilterType.value,
    }

    dispatch(fetchStoryboards(request));
  }

  const handleCreateStoryboardClick = () => {
    if (!promptBasedEditorEnabled) {
      setState(prev => ({ ...prev, create: true }));
      return;
    }
  
    const url = listId
      ? `${baseUrl}/storyboards/creation/folder/${listId}`
      : `${baseUrl}/storyboards/creation`;
  
    history.push(url);
  };

  const handleDeleteClick = (storyboard) => {
    setState((state) => {
      return {
        ...state,
        delete: storyboard
      };
    });
  }

  const closePopups = () => {
    setState((state) => {
      return {
        ...state,
        create: false,
        delete: null
      };
    });
  };


  useEffect(() => {
    const handleScroll = () => {
      var header = document.getElementById("smartFolderContainer");
      var sticky = header.offsetTop;
      const scrollTop = scrollContainerRef.current.scrollTop;

      if (scrollTop > sticky) {
        setStickHeader(true)
      } else {
        setStickHeader(false)
      }
    };

    scrollContainerRef.current.addEventListener('scroll', handleScroll);

    return () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener('scroll', handleScroll);
      }
    }
  }, []);

  const CustomFilterDropdown = React.forwardRef(({ children, onClick, className, ...rest }, ref) => (
    <div
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      className={styles.dropDownToggle}
      {...rest}
    >
      <span>
        {children}
      </span>
      <i className={cx('cleverstory-drop-down')}/>
      </div>
  ));

  const importDropdown = React.forwardRef(({ children, onClick }, ref) => (
    <Button variant="primary"
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      className={cx(styles.dropDownToggle, { [styles.active] : showImportDropdown })}
    >
      {children}
    </Button>
  ));

  const canShowFilter = (type) => {
    if(INTERACTIVE_CONTENT_TYPES.includes(type)) {
      return subscription.interactiveContentEnabled;
    }

    if(type === 'LANDING_PAGE') {
      return subscription.landingPageEnabled;
    }

    if(type === 'RESOURCE_CENTER') {
      return subscription.resourceCenterEnabled;
    }

    return true;
  }

  const updateProperty = (fieldName, value) => {
    setModalState((state)=>{
      return{
        ...state,
        [fieldName]:value
      }
    })
  }

  const resetAllModals = () => {
    setModalState({});
  }

  const invalidatePpt = () => {
    setPptState({});
  }

  useEffect(()=>{
    dispatch(fetchIntegrations());
  },[])

  return (
    <>
      <div className={styles.content} ref={scrollContainerRef}>
        <header className={styles.header}>
          <aside className={styles.headerAside}>
            {workspaces.length > 1 &&
              <span className={styles.workspaceName}>{workspace.name}</span>
            }
            <h1 className={styles.storyboardHeading}>My Storyboards</h1>
            <span className={styles.storyboardSummary}>
              Everything you created, topped with stardust
            </span>
          </aside>

          {userRoleService.canCreateStoryboard() &&
            <>
              <Dropdown 
                className={styles.importDropdown} 
                show={showImportDropdown} 
                onMouseEnter={()=>setShowImportDropdown(true)} 
                onMouseLeave={()=>setShowImportDropdown(false)}
              >
                <Dropdown.Toggle as={importDropdown} className={cx(styles.importButton)}>
                  <i className={cx('cleverstory-import')} />Import
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {subscription.interactiveContentEnabled &&
                    <Dropdown.Item data-track-event="Mixpanel" data-track-props="PPT Import from Home">
                      <PPTXButton 
                        setPptState = {setPptState} 
                        setCreateState={setState} 
                        storyboardState={storyboardState}
                        setStoryboardState={setStoryboardState}
                        createDirectly={false}
                        className={styles.pptxButton}
                      />
                    </Dropdown.Item>
                  }
                  <Dropdown.Item 
                    onClick={()=>updateProperty('showLinkModal', true)}
                    data-track-event="Mixpanel" data-track-props="Figma Import from Home" 
                  >
                    <i className={cx('cleverstory-figma')} />Figma
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </>
          }
        </header>
        <div className={styles.secondaryHeader}>
          <aside className={styles.filterContainer}>
            <label className={styles.filterLabel}>Filter by:</label>
            <div className={styles.dropDownWrapper}>
              <Dropdown className={styles.storboardTypeDropdown}>
                <Dropdown.Toggle as={CustomFilterDropdown} id="dropdown-custom-components" data-track-event="Mixpanel" data-track-props="Content Type Filter">
                  {filterType.label}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {
                    filterTypeOption.map((option, index) => {
                      if (!canShowFilter(option.value)) {
                        return null;
                      }

                      return (
                        <Dropdown.Item active={option.value == filterType.value} onClick={() => setFilterType(option)} key={index} eventKey={index}>{option.label}</Dropdown.Item>
                      )
                    })
                  }
                </Dropdown.Menu>
              </Dropdown>
              <Dropdown className={styles.liveDraftDropdown}>
                <Dropdown.Toggle as={CustomFilterDropdown}>
                  {draftFilterType.label}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {draftFilterOptions.map((option, index) => (
                    <Dropdown.Item
                      key={index}
                      active={option.value === draftFilterType.value}
                      onClick={() => setDraftFilterType({
                        label: option.label,
                        value: option.value,
                      })}
                      eventKey={index}
                    >
                      {option.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </aside>

          <aside>
            <InputGroup className={styles.searchBox}>
              <InputGroup.Text className={styles.searchIcon} id="basic-addon1">
                <i className="cleverstory-search" />
              </InputGroup.Text>
              <Form.Control
                className={styles.searchInput}
                placeholder="Search"
                aria-label="Search"
                value={searchQuery}
                onChange={(e)=> setSearchQuery(e.target.value)}
                aria-describedby="basic-addon1"
              />
            </InputGroup>
          </aside>
        </div>
        <section className={styles.storyboards}>
          <StoryboardSmartList
            sticky={stickHeader}
          />

          {loading && storyboards.length === 0 &&
            <Loader className={styles.loadingAnimation} title="Loading storyboards..." />
          }

          {storyboards.length > 0 &&
            <InfiniteScroll
              className={styles.scrollable}
              loading={loading}
              hasMore={hasMoreItems}
              loadMore={() => {
                loadMoreStoryboards();
              }}
            >
              {userRoleService.canCreateStoryboard() &&
                <div
                  onClick={handleCreateStoryboardClick}
                  className={cx(styles.storyboardCard, styles.create)}
                  data-track-event="Mixpanel" data-track-props="Create new Storyboard"
                >
                  <div className={styles.contents}>
                    <i className={cx(styles.plus, "cleverstory-add")} />
                    <span>Create New Storyboard</span>
                  </div>
                </div>
              }

              {(uploads || []).map((upload, index) => {
                if(_.get(upload,'fileType') !== 'figma')
                  return (
                    <PPTLoaderCard key={index} upload={upload} />
                  )
              })}

              {(uploads || []).map((upload, index) => {
                if(_.get(upload,'fileType') === 'figma')
                  return (
                    <FigmaLoaderCard key={index} upload={upload} />
                  )
              })}

              {storyboards.map((item, index) => {
                return (
                  <StoryboardCard key={item.id} storyboard={item} onDelete={handleDeleteClick} />
                );
              })}
            </InfiniteScroll>
          }

          {!loading && storyboards.length === 0 &&
            <div className={styles.emptyMessageContainer}>
              <div className={styles.emptyMessage}>
                <video className={styles.emptyImage} loop autoPlay playsInline muted>
                  <source src="/images/Blackhole.webm" type="video/webm" />
                  <source src="/images/Blackhole.mp4" type="video/mp4" />
                </video>
                <div className={styles.errorMessageContent}>
                  <div className={styles.errorMessageTitle}>
                    <span>Do<br /> you believe in</span>
                    <span>Black<br /> Holes?</span>
                  </div>
                  <div className={styles.errorMessageSubText}>
                    <span>You better do, because nothing<br /> else explains this empty screen.<br /></span>
                    <span>...That or you haven’t added <br /> anything here yet.</span>
                  </div>
                </div>
              </div>
              {userRoleService.canCreateStoryboard() && <Button
                onClick={handleCreateStoryboardClick}
                variant="primary"
              >
                <i className={cx(styles.addIcon, "cleverstory-plus")} />
                Create new Storyboard
              </Button>}
            </div>
          }
        </section>
        <CreateStoryboard 
          show={state.create} 
          onClose={closePopups} 
          updateProperty={updateProperty} 
          setPptState={setPptState} 
          setCreateState={setState}
          storyboardState={storyboardState}
          setStoryboardState={setStoryboardState}
        />
        <DeleteStoryboard storyboard={state.delete} onClose={closePopups} />
      </div>
      <StoryboardCustomDragLayer />

    <FigmaLinkModal 
      show={modalState.showLinkModal} 
      onDiscard={()=>updateProperty('showLinkModal', false)} 
      updateProperty={updateProperty}
    />

    <FigmaFramesModal 
      show={modalState.showFramesModal} 
      onDiscard={()=>updateProperty('showFramesModal', false)} 
      updateProperty={updateProperty} 
    />
    
    <FontsModal 
      show={modalState.showFontsModal} 
      onDiscard={()=>updateProperty('showFontsModal', false)} 
      resetAllModals={resetAllModals}
      updateProperty={updateProperty}
      storyboardState={storyboardState}
      setStoryboardState={setStoryboardState}
      setState={setState}
    />

    <ChooseContentTypePanel 
      show={modalState.showContentTypePanel} 
      onDiscard={()=>updateProperty('showContentTypePanel', false)} 
      resetAllModals={resetAllModals}
    />

    <ChoosePPTContentTypePanel 
      show={pptState.showPPTModal} 
      pptFile={pptState.pptFile} 
      onDiscard={invalidatePpt}
    />

  </>
  )
}

const PPTLoaderCard = ({ upload }) => {
  const [tooltip, setTooltip] = useState(false);

  const handleTooltip = (truncated) => {
    const { clamped } = truncated;
    setTooltip(clamped);
  }

  return (
    <div className={cx(styles.storyboardCard, styles.pptCard)}>
      <img className={styles.icon} src={'/images/storyboard.png'} alt="" />

      <div className={styles.details}>
        <div className={styles.top}>
          <Tooltip placement="top" disabled={!tooltip} content={upload.name}>
            <span className={styles.title}>
              <LinesEllipsis basedOn="words" onReflow={handleTooltip} text={upload.name} maxLine='2' ellipsis='...' />
            </span>
          </Tooltip>
        </div>

        <div className={styles.statusContainer}>
          <p className={styles.contentType}>{upload.storyboardType}</p>
          <span className={styles.pptStatus}>{upload.prosessing ? 'Processing' : 'Uploading'}</span>
        </div>
        
        <ProgressBar variant="warning" className={styles.progressBar} now={upload.progress} />
      </div>
    </div>
  )
}

const FigmaLoaderCard = ({ upload }) => {
  const description = useSelector(state => _.get(state, 'integrations.figma.socket.description', "Connecting to Figma"));
  const progress = useSelector(state => _.get(state, 'integrations.figma.socket.progress', 0));
  const [tooltip, setTooltip] = useState(false);

  const handleTooltip = (truncated) => {
    const { clamped } = truncated;
    setTooltip(clamped);
  }

  return (
    <div className={cx(styles.storyboardCard, styles.figmaCard)}>
      <img className={styles.icon} src={'/images/storyboard.png'} alt="" />

      <div className={styles.details}>
        <div className={styles.top}>
          <Tooltip placement="top" disabled={!tooltip} content={upload.name}>
            <span className={styles.title}>
              <LinesEllipsis basedOn="words" onReflow={handleTooltip} text={upload.name} maxLine='2' ellipsis='...' />
            </span>
          </Tooltip>
        </div>

        <div className={styles.statusContainer}>
          <p className={styles.contentType}>{upload.storyboardType}</p>
          {upload.processing && (
            <span className={styles.figmaStatus}>{description}</span>
           )}
        </div>

        <div className={styles.loadingBar}>
        <ProgressBar variant="warning" className={styles.progressLoader} now={progress} />
        </div>
      </div>
    </div>
  )
}

const StoryboardCard = ({ storyboard, onDelete }) => {
  const history = useHistory();
  const [selectedDomain, setSelectedDomain] = useState({});
  const [cloneStoryboard, setCloneStoryboard] = useState(null);

  let { path, url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  const icon = _.get(storyboard, 'page.icon.url');
  const published = _.get(storyboard, 'page.published');
  const type = _.get(storyboard, 'type', '');

  var domain = useSelector(state => _.get(state, 'workspaces.workspace.item.defaultDomain') || "");
  const canonical = _.get(storyboard, 'page.canonical') || "";
  const liveUrl = `${_.get(selectedDomain, 'url')}${canonical}`;
  const changes = _.get(storyboard, 'page.changes');
  const { listId } = useParams();

  const [{ opacity }, dragRef, dragPreview] = useDrag(
    () => ({
      type: "storyboard",
      item: {
        type: "storyboard",
        sourceList: listId,
        storyboard: storyboard
      },
      collect: (monitor) => ({
        opacity: monitor.isDragging() ? 0.5 : 1
      })
    }),
    []
  )

  useEffect(() => { // this useEffect hides the default preview
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  useEffect(()=>{
    let domainUrl = _.get(storyboard,'domain') || domain;
    setSelectedDomain(domainUrl);
  },[storyboard, domain])

  const handleEditClick = () => {
    if (!storyboardRoleService.canEditStoryboard(storyboard)) {
      return;
    }

    var page = storyboard.page;

    if (page) {
      const win = window.open(`${baseUrl}/pages/${page.id}`, "_blank");
      win.focus();
    }
  };

  const handleSettingsClick = () => {
    const url = `${baseUrl}/storyboards/${storyboard.id}/website-settings`;
    history.push(url)
  }

  const handlePreviewPage = () => {
    var page = storyboard.page;
    if (!page) {
      return;
    }

    const url = `${baseUrl}/pages/${page.id}/preview`;
    const win = window.open(url, "_blank");
    win.focus();
  }

  const handleDelete = (storyboard) => {
    onDelete(storyboard);
  }

  const handleClone = (storyboard) => {
    setCloneStoryboard({
      name: storyboard.name,
      id: storyboard.id
    });
  }

  const createClone = (storyboardName) => {
    // Ensure the base for relative URLs
    const url = new URL(
      `${baseUrl}/storyboards/${cloneStoryboard.id}/clone`,
      window.location.origin
    );

    if (listId) {
      url.searchParams.append("list", listId);
    }

    if (storyboardName) {
      url.searchParams.append("name", storyboardName);
    }

    setCloneStoryboard(null);

    const win = window.open(url.toString(), "_blank");
    win?.focus();
  };

  const handleLiveSiteClicked = () => {
    if (canonical) {
      const win = window.open(liveUrl, "_blank");
      win.focus();
    }
  }

  const handleInviteUsersClicked = () => {
    const url = `${baseUrl}/storyboards/${storyboard.id}/permissions`;
    history.push(url);
  }

  const renderStoryboardType = () => {
    switch (type) {
      case STORYBOARD_TYPES.LANDING_PAGE:
        return 'Landing page';
      case STORYBOARD_TYPES.RESOURCE_CENTER:
        return 'Resource center';
      case STORYBOARD_TYPES.SPATIAL:
        return 'Spatial';
      case STORYBOARD_TYPES.FLIP_BOOK:
        return 'Flipbook';
      case STORYBOARD_TYPES.INTERACTIVE_CONTENT:
      case STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2:
        return 'Interactive experience';
      default:
        return '';
    }
  }

  return (
    <>
      <div className={styles.storyboardCard} ref={dragRef} style={{ opacity }}>
        {icon &&
          <IKImage
            className={styles.icon}
            path={icon}
            transformation={[{
              "width": "600"
            }]}
            onClick={handleSettingsClick}
          />
        }
        {!icon &&
          <img className={styles.icon} onClick={handleSettingsClick} src={'/images/storyboard.png'} alt="" />
        }

        <div className={styles.details}>
          <div className={styles.top}>
            <TruncatedTextWithTooltip placement="top" content={storyboard.name} className={styles.title}>
              <span>{storyboard.name}</span>
            </TruncatedTextWithTooltip>
            {published &&
              <label className={cx(styles.status, styles.live)}>LIVE</label>
            }
          </div>

          <p className={styles.contentType}>{renderStoryboardType()}</p>

          <div className={styles.updatedDate}>
            Last Updated: {dateformat(_.get(storyboard, 'page.modifiedDate'), "dd mmm yyyy")}
            {(changes || !published) &&
              <label className={cx(styles.status, styles.draft)}>DRAFT</label>
            }
          </div>

          <div className={styles.actions}>
            {storyboardRoleService.canEditStoryboard(storyboard) &&
              <Tooltip placement="top" content="Edit">
                <Button className={styles.actionGroup} variant="info" onClick={handleEditClick} data-track-event="Mixpanel" data-track-props="Edit from Home">
                  <i className={cx("cleverstory-edit")} />
                </Button>
              </Tooltip>
            }

            <Tooltip placement='top' content='Preview'>
              <Button className={styles.actionGroup} variant="info" onClick={handlePreviewPage} data-track-event="Mixpanel" data-track-props="Preview from Home">
                <i className={cx("cleverstory-view", styles.viewIcon)} />
              </Button>
            </Tooltip>

            {storyboardRoleService.canEditStoryboard(storyboard) && userRoleService.canCreateStoryboard() &&
              <Tooltip placement='top' content='Clone'>
                <Button className={styles.actionGroup} variant="info" onClick={() => handleClone(storyboard)}>
                  <i className={cx("cleverstory-clone")} />
                </Button>
              </Tooltip>
            }

            {published && liveUrl &&
              <Tooltip placement='top' content={liveUrl}>
                <Button className={styles.actionGroup} variant="info" onClick={() => handleLiveSiteClicked(storyboard)}>
                  <i className={cx("cleverstory-url")} />
                </Button>
              </Tooltip>
            }
          {storyboardRoleService.canManageStoryboard(storyboard) &&
            <OverlayTrigger rootClose trigger="click" placement="top" overlay={
              <Popover className={styles.storyboardCardMoreAction} id="popover-basic">
                <ListGroup>
                    <ListGroup.Item className={styles.storyboardCardMoreList} onClick={handleInviteUsersClicked}>Invite People</ListGroup.Item>

                    <ListGroup.Item className={styles.storyboardCardMoreList}
                      onClick={() => handleDelete(storyboard)}>Delete</ListGroup.Item>
                </ListGroup>
              </Popover>
            }>
              <Button className={cx(styles.more, styles.action)} variant="info">
                <i className={cx("cleverstory-more")} />
              </Button>
            </OverlayTrigger>
            }
          </div>
        </div>
      </div>
      {cloneStoryboard && <StoryboardClone
        id={cloneStoryboard.id}
        name={cloneStoryboard.name}
        createClone={createClone}
        cancelModal={() => setCloneStoryboard(null)}
      />}
    </>

  );
};

const StoryboardClone = ({
  id,
  name,
  createClone,
  cancelModal
}) => {
  const [storyboardName, setStoryboardName] = useState(`Copy of ${name}`);

  return (
    <Offcanvas className={cx(styles.cloneStoryboard)} show={id} placement="end">
      <i onClick={cancelModal} className={cx(styles.close, "cleverstory-close")} />
      <header className={styles.cloneHeader}>
        <h1>Clone Storyboard</h1>
        <p>
          {`Would you like to make a copy of `}
            <Truncate lines={1}>
              {name}
            </Truncate>
          {`?`}
        </p>
        <input
          name={storyboardName} 
          value={storyboardName}
          onChange={(e) => setStoryboardName(e.target.value)}
        />
      </header>
      <footer className={styles.cloneActions}>
        <Button variant='secondary' 
          type="reset" 
          onClick={cancelModal}
        >
          Cancel
        </Button>
        <Button variant='primary' 
          type="submit"
          className={!storyboardName.trim() && cx(styles.buttonDisabled)}
          disabled={!storyboardName.trim()}
          onClick={() => createClone(storyboardName)}
        >
          Clone Storyboard
        </Button>
      </footer>
    </Offcanvas>
  );
};
